import { useEffect, useState } from "react";
import Select from "react-select";
import { selector } from "../../api";
import { boundaryCRUD } from "../../actions/boundary";

/**
 * Reusable Region -> District -> Shehia cascading boundary filter.
 * Mirrors the cascade in settlementOverview.jsx.
 *
 * Props:
 *   onChange(boundaryId, boundaryName)  - called whenever the selection changes.
 *
 * Notes:
 *   - loadBoundaryByIsland returns resp.data.results; loadBoundaryByParent returns resp.data
 *     (raw array, NOT .results) — matched exactly to avoid the known gotcha.
 *   - Only staff/national users get the Island selector; others are already boundary-scoped
 *     by the backend, so the filter simply narrows within their own scope.
 */
export const BoundaryCascade = ({ onChange }) => {
  const user = selector.User();
  const isAdmin = user?.is_staff;

  const [islands, setIslands] = useState([]);
  const [regions, setRegions] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [localities, setLocalities] = useState([]);

  const [selectedIsland, setSelectedIsland] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedLocality, setSelectedLocality] = useState(null);

  useEffect(() => {
    if (isAdmin) {
      boundaryCRUD.loadAllIslands().then((resp) => setIslands(resp.data)).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    setSelectedRegion(null); setRegions([]); setDistricts([]); setLocalities([]);
    if (selectedIsland) {
      boundaryCRUD.loadBoundaryByIsland(1000, 0, "Region", selectedIsland)
        .then((resp) => setRegions(resp.data.results)).catch(() => {});
    }
  }, [selectedIsland]);

  useEffect(() => {
    setSelectedDistrict(null); setDistricts([]); setLocalities([]);
    if (selectedRegion) {
      boundaryCRUD.loadBoundaryByParent(selectedRegion)
        .then((resp) => setDistricts(resp.data)).catch(() => {});
    }
  }, [selectedRegion]);

  useEffect(() => {
    setSelectedLocality(null); setLocalities([]);
    if (selectedDistrict) {
      boundaryCRUD.loadBoundaryByParent(selectedDistrict)
        .then((resp) => setLocalities(resp.data)).catch(() => {});
    }
  }, [selectedDistrict]);

  // Report the deepest selection (id + name) to the parent whenever it changes.
  useEffect(() => {
    let id = null, name = null;
    const find = (arr, sid, key) => arr.filter((x) => x.id === sid).map((x) => x[key])[0];
    if (selectedLocality) { id = selectedLocality; name = find(localities, selectedLocality, 'boundary_name'); }
    else if (selectedDistrict) { id = selectedDistrict; name = find(districts, selectedDistrict, 'boundary_name'); }
    else if (selectedRegion) { id = selectedRegion; name = find(regions, selectedRegion, 'boundary_name'); }
    else if (selectedIsland) { id = selectedIsland; name = find(islands, selectedIsland, 'name'); }
    if (onChange) onChange(id, name);
  }, [selectedIsland, selectedRegion, selectedDistrict, selectedLocality]);

  const opt = (arr, key = 'boundary_name') => arr?.map((d) => ({ value: d.id, label: d[key] }));
  const cur = (arr, sid, key = 'boundary_name') =>
    sid ? arr.filter((x) => x.id === sid).map((x) => ({ value: x.id, label: x[key] }))[0] : null;

  return (
    <div className="row g-2 mb-3">
      {isAdmin && (
        <div className="col-md-3">
          <label className="form-label fw-semibold">Island</label>
          <Select options={opt(islands, 'name')} value={cur(islands, selectedIsland, 'name')}
            placeholder="Select island" onChange={(e) => setSelectedIsland(e?.value || null)} isClearable isSearchable />
        </div>
      )}
      <div className="col-md-3">
        <label className="form-label fw-semibold">Region</label>
        <Select options={opt(regions)} value={cur(regions, selectedRegion)}
          placeholder="Select region" onChange={(e) => setSelectedRegion(e?.value || null)}
          isDisabled={isAdmin && !selectedIsland} isClearable isSearchable />
      </div>
      <div className="col-md-3">
        <label className="form-label fw-semibold">District</label>
        <Select options={opt(districts)} value={cur(districts, selectedDistrict)}
          placeholder="Select district" onChange={(e) => setSelectedDistrict(e?.value || null)}
          isDisabled={!selectedRegion} isClearable isSearchable />
      </div>
      <div className="col-md-3">
        <label className="form-label fw-semibold">Shehia</label>
        <Select options={opt(localities)} value={cur(localities, selectedLocality)}
          placeholder="Select shehia" onChange={(e) => setSelectedLocality(e?.value || null)}
          isDisabled={!selectedDistrict} isClearable isSearchable />
      </div>
    </div>
  );
};

export default BoundaryCascade;
